import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Stack,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  getAllLanguages,
  getAllSubjects,
  getBundlesByTrade,
  getOneSubject,
  getTrades,
  postQuiz,
  updateQuiz,
} from "../../api/services";
import FormMultiSelect from "../../components/FormFields/FormMultiSelect";
import Quill from "../../components/Quill";
import SingleSelect from "../../components/forms/SingleSelect";
import { TextField } from "../../components/forms/TextField";
import { AddQuizValidation } from "../../validations/quiz";

// function formatDate(date: any) {
//   const d = new Date(date);
//   return format(
//     addMinutes(d, d.getTimezoneOffset()),
//     'yyyy-MM-dd HH:mm:ss',
//   ).replace(' ', 'T');
// }

const validationSchema = Yup.object().shape({
  title: Yup.string().required("The title field is required"),
});

export default function QuizForm({ closeModal, data }: any) {
  const queryClient = useQueryClient();

  const { handleSubmit, control, setValue, watch } = useForm({
    defaultValues: {
      tradeId: data?.tradeId ?? "",
      bundleId: data?.bundleId ?? "",
      yearId: data?.yearId ?? "",
      languageId: data?.languageId ?? "",
      subjectId: data?.subjectId ?? "",
      chapterIds: data?.chapters?.map((c: any) => c.chapterId) ?? [],
      name: data?.name ?? "",
      isRandom: data?.isRandom ?? true,
      totalQuestions: data?.totalQuestions ?? "",
      marksPerQuestion: data?.marksPerQuestion ?? 0,
      hasNegativeMarking: data?.hasNegativeMarking ?? false,
      negativeMark: data?.negativeMark ?? 0,
      isGraded: data?.isGraded ?? false,
      maxAttempts: data?.maxAttempts ?? "",
      passPercentage: data?.passPercentage ?? 0,
      instructions: data?.instructions ?? "",
      description: data?.description ?? "",
    },
    resolver: yupResolver(AddQuizValidation),
  });

  console.log("data?.chapters :>> ", data?.chapters);

  useEffect(() => {
    setValue("negativeMark", 0);
  }, [watch("hasNegativeMarking")]);

  useEffect(() => {
    if (!data) {
      setValue("chapterIds", []);
    }
  }, [watch("subjectId")]);

  console.log(
    data?.chapters?.map((c: any) => c.chapterId),
    "data?.chapters?.map((c:any)=>c.chapterId)"
  );

  const { mutate: createMutate } = useMutation(postQuiz, {
    onSuccess: (res: any) => {
      queryClient.invalidateQueries("quiz-details");
      toast.success("Quiz created successfully");
      closeModal();
    },
    onError: (err: any) => {
      toast.error(err.response.data.message);
    },
  });

  const { mutate: updateMutate } = useMutation(updateQuiz, {
    onSuccess: (res: any) => {
      toast.success("Quiz updated successfully");
      queryClient.invalidateQueries("quiz");
      closeModal();
    },
    onError: (err: any) => {
      toast.error(err.response.data.message);
    },
  });

  const { data: tradeData } = useQuery("trades", getTrades);

  const { data: languageData } = useQuery("languages", getAllLanguages);

  const { data: bundleYears } = useQuery(
    ["bundle-years", watch("tradeId")],
    getBundlesByTrade
  );

  const { data: subjectsData } = useQuery(
    ["getAllSubjects", { languageId: watch("languageId") }],
    getAllSubjects,
    {
      enabled: !!watch("languageId"),
    }
  );

  const { data: chaptersData } = useQuery(
    ["chapters", watch("subjectId")],
    getOneSubject,
    {
      enabled: !!watch("subjectId"),
    }
  );

  const trades = tradeData?.data?.data?.map((item: any) => ({
    value: item?._id,
    label: item?.name,
  }));

  const languages = languageData?.data?.data?.map((item: any) => ({
    value: item?._id,
    label: item?.name,
  }));

  const years = bundleYears?.data?.years?.map((item: any) => ({
    value: item?._id,
    label: item?.name,
  }));

  const bundles = bundleYears?.data?.bundles?.map((item: any) => ({
    value: item?._id,
    label: item?.name,
  }));

  const subjects = subjectsData?.data?.data?.map((i: any) => ({
    value: i?._id,
    label: i?.title,
  }));

  const chapters = chaptersData?.data?.chapters?.map((c: any) => ({
    value: c?._id,
    label: c?.title,
  }));

  const onSubmit = (formData: any) => {
    if (data?._id) {
      updateMutate({ id: data?._id, body: formData });
    } else {
      createMutate(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={2}>
        <SingleSelect
          label="Trade"
          name="tradeId"
          control={control}
          options={trades}
        />
        <SingleSelect
          label="Bundle Type"
          name="bundleId"
          control={control}
          options={bundles}
        />

        <SingleSelect
          label="Year"
          name="yearId"
          control={control}
          options={years}
        />
        <SingleSelect
          label="Language"
          name="languageId"
          control={control}
          options={languages}
        />

        <SingleSelect
          label="Curriculum"
          name="subjectId"
          control={control}
          options={subjects}
        />

        <FormMultiSelect
          label="Chapter"
          name="chapterIds"
          control={control}
          options={chapters}
          size="medium"
        />

        <TextField name="name" label="Title" control={control} />

        <FormControlLabel
          control={
            <Controller
              name="isRandom"
              control={control}
              render={({ field }) => (
                <Checkbox {...field} checked={watch("isRandom")} />
              )}
            />
          }
          label="Add Questions Randomly"
        />

        <TextField
          name="totalQuestions"
          muiProps={{ type: "number" }}
          label="Total Number of Questions"
          control={control}
        />

        <TextField
          name="marksPerQuestion"
          muiProps={{ type: "number" }}
          label="Marks Per Question"
          control={control}
        />

        <FormControlLabel
          control={
            <Controller
              name="hasNegativeMarking"
              control={control}
              render={({ field }) => (
                <Checkbox {...field} checked={watch("hasNegativeMarking")} />
              )}
            />
          }
          label="Has Negative Marking"
        />

        {watch("hasNegativeMarking") && (
          <TextField
            muiProps={{ type: "number" }}
            name="negativeMark"
            label="Negavite Mark Per Question"
            control={control}
          />
        )}

        <FormControlLabel
          control={
            <Controller
              name="isGraded"
              control={control}
              render={({ field }) => (
                <Checkbox {...field} checked={watch("isGraded")} />
              )}
            />
          }
          label="Graded"
        />

        <TextField
          muiProps={{ type: "number" }}
          name="maxAttempts"
          label="Maximum Attempts"
          control={control}
        />

        <TextField
          muiProps={{ type: "number" }}
          name="passPercentage"
          label="Pass Percentage"
          control={control}
        />

        {/* <FormControlLabel
          control={
            <Controller
              name="timedTest"
              control={control}
              render={({ field }) => (
                <Checkbox {...field} checked={watch('timedTest')} />
              )}
            />
          }
          label="Is Scheduled Test"
        />
        {watch('timedTest') && (
          <Stack direction="row" gap={2}>
            <DateTimePicker
              label="Start Date Time"
              value={watch('startsAt')}
              onChange={(value) => setValue('startsAt', value)}
              renderInput={(params) => (
                <MuiTextField
                  fullWidth
                  required={watch('timedTest')}
                  {...params}
                />
              )}
            />
            <DateTimePicker
              label="End Date Time"
              value={watch('endsAt')}
              onChange={(value) => setValue('endsAt', value)}
              renderInput={(params) => (
                <MuiTextField
                  fullWidth
                  required={watch('timedTest')}
                  {...params}
                />
              )}
            />
          </Stack>
        )} */}

        <FormControl>
          <FormLabel>Instructions</FormLabel>
          <Quill
            placeholder="Instructions"
            data={watch("instructions") || ""}
            handleChange={(value) => setValue("instructions", value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Description</FormLabel>
          <Quill
            placeholder="Description"
            data={watch("description") || ""}
            handleChange={(value) => setValue("description", value)}
          />
        </FormControl>

        <Box>
          <Button type="submit" variant="contained">
            Submit
          </Button>
        </Box>
      </Stack>
    </form>
  );
}
